"use client";

import { useEffect } from "react";
import { motion } from "motion/react";
import { Check } from "lucide-react";
import { SubpageChrome } from "@/components/pages/SubpageChrome";
import { SubpageContactBand } from "@/components/pages/SubpageContactBand";
import { useContent } from "@/components/site/ContentProvider";
import pageStyles from "./subpage.module.css";
import styles from "./services-page.module.css";

function readServiceHash(): string {
  try {
    return decodeURIComponent(window.location.hash.replace(/^#/, "")).trim();
  } catch {
    return "";
  }
}

export function ServicesPageView() {
  const site = useContent();
  const page = site.pages.services;

  useEffect(() => {
    const run = () => {
      const id = readServiceHash();
      if (!id) return;
      if (!site.services.some((s) => s.id === id)) return;
      const el = document.getElementById(id);
      if (!el) return;

      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      el.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "center",
      });
      if (reduceMotion) return;

      el.classList.remove(styles.outlinePulse);
      // Retrigger CSS animation if landing again with the same hash.
      void el.offsetWidth;
      el.classList.add(styles.outlinePulse);

      const onEnd = (event: AnimationEvent) => {
        if (event.target !== el) return;
        el.classList.remove(styles.outlinePulse);
        el.removeEventListener("animationend", onEnd);
      };
      el.addEventListener("animationend", onEnd);
    };

    // Defer past first paint / header so cold hash landings still pulse.
    const t = window.setTimeout(run, 120);
    window.addEventListener("hashchange", run);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("hashchange", run);
    };
  }, [site.services]);

  return (
    <SubpageChrome path="services">
      <div className={pageStyles.hero}>
        <span className={pageStyles.eyebrow}>{page.title}</span>
        <h1 className={pageStyles.headline}>{page.headline}</h1>
        <p className={pageStyles.subhead}>{page.subhead}</p>
      </div>

      <div className={styles.grid}>
        {site.services.map((service, i) => (
          <motion.article
            key={service.id}
            className={styles.card}
            id={service.id}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-8% 0px" }}
            transition={{
              duration: 0.55,
              delay: Math.min(i * 0.05, 0.25),
              ease: [0.22, 0.61, 0.36, 1],
            }}
          >
            <div className={styles.media}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={service.image}
                alt={service.imageAlt}
                className={styles.image}
                loading={i < 2 ? "eager" : "lazy"}
              />
            </div>
            <div className={styles.body}>
              <span className={styles.num} dir="ltr">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h2 className={styles.title}>{service.name}</h2>
              <p className={styles.desc}>{service.description}</p>
              <ul className={styles.list}>
                {service.highlights.map((item) => (
                  <li key={item} className={styles.item}>
                    <Check
                      className={styles.check}
                      strokeWidth={1.75}
                      aria-hidden
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.article>
        ))}
      </div>

      <SubpageContactBand band={page.band} headingId="services-band" />
    </SubpageChrome>
  );
}
